import ModalView from './modal.view'
import Modal from './modal'
import { PubsubEvent } from '../pubsub/pubsub'
import { NavState } from '../nav/nav'
import Msg from '../messenger/messenger'
import { ModalSDK } from './modal.types'
import { ClassyURLParams } from '../params/params.types'
import {
  EMBEDDED_APP_VISIBLE,
  DONATION_CLOSE_REQUESTED_MSG_TO_APP,
  DONATION_READY_MSG_FROM_APP,
  DONATION_PARAMS_UPDATE,
  SDK_INIT,
  HostPageEventsMsgToApp,
  INLINE_GRID_TELEMETRY_MSG_TO_APP,
  MsgType,
} from '../messenger/messenger.messages'
import { Configs } from '../config/config.service'
import { ConfigResponse } from '../config/config.types'
import { CLASSY_ATTR, CLASSY_HREF } from '../crawler/crawler.constants'
import HostPageEventInstrumentation, {
  createHostPageEventInstrumentation,
} from '../host-page-events/host-page-event.instrumentation'
import { eg3SDKLoadEvent } from '../host-page-events/host-page-event.definitions'
import type { AnalyticsEventData } from '../analytics/AnalyticsManager'
import { isInlineGridTelemetry, InlineGridTelemetry } from '../inline-grid/inline-grid-telemetry'

interface ModalCtrlProps {
  getInlineGridParams?: (campaignId: string) => ClassyURLParams | undefined
  msg: Msg
  win: Window
  configs: Promise<Configs>
  pgdid?: string
  sendAnalyticsEvent?: (eventName: string, eventData: AnalyticsEventData) => void
}

type ModalViewVisibility = 'hidden' | 'visible'

export default class ModalCtrl {
  private props: ModalCtrlProps
  private view?: ModalView
  private modal: Modal
  private destroyed = false
  /**
   * Sole view storage, keyed by campaign id. Holds every ModalView we
   * have created — preloaded, hover-cached, or opened directly — so the
   * iframe is never rebuilt for a campaign we've already seen.
   */
  private cachedViews: Map<string, ModalView> = new Map()
  /**
   * Subset of `cachedViews` keys whose iframe was created by `_preloadIframes`
   * and has not yet been activated by a user-initiated open. Membership
   * means "this iframe's DONATION_READY handshake is owned by the preload
   * listener, not the shared messenger" — so `onDonationReady` skips the
   * regular SDK_INIT flow for these. Entries are removed on first
   * `_activateCampaign`, after which the view stays in `cachedViews` and
   * follows the regular messenger-driven flow.
   */
  private unopenedPreloads: Set<string> = new Set()
  private preloadListener?: (event: MessageEvent) => void
  private pendingHostPageEventMessages: Map<string, HostPageEventsMsgToApp[]> = new Map()
  private pendingInlineGridTelemetry: InlineGridTelemetry[] = []
  /** Params from the most recent open — resent when onDonationReady fires. */
  private pendingParams?: ClassyURLParams
  private pendingInlineGridParams?: ClassyURLParams
  private hostPageEventInstrumentation: HostPageEventInstrumentation
  private eg3SDKLoadTrackedCampaigns = new Set<string>()
  private readyCampaigns = new Set<string>()
  /**
   * Campaigns whose embedded app declared it can handle close requests
   * (DONATION_CLOSE_INTERCEPT_READY). Closing the modal for these campaigns
   * defers to the app via DONATION_CLOSE_REQUESTED until it answers with
   * DONATION_CLOSE_CONFIRMED.
   */
  private closeInterceptReadyCampaigns = new Set<string>()
  private reinitializingCampaigns = new Set<string>()
  /**
   * Campaigns whose active document finished a donation. The document stays
   * ready (the thank-you screen is still alive and listening), but the view
   * must not be resurrected for a new donation: deactivating removes it
   * instead of hiding it, and an in-place reload (e.g. "Make another
   * donation") re-runs the SDK_INIT handshake via _handleIframeLoad.
   */
  private completedCampaigns = new Set<string>()
  private loadedViews = new WeakSet<ModalView>()

  constructor(props: ModalCtrlProps) {
    this.props = props

    this.hostPageEventInstrumentation = createHostPageEventInstrumentation({
      sendAnalyticsEvent: props.sendAnalyticsEvent,
      sendMessage: this._sendHostPageEventMessage,
    })

    this.modal = new Modal({
      win: props.win,
      shouldInterceptClose: this._shouldInterceptClose,
      onCloseRequested: this._sendDonationCloseRequestedEvent,
    })
    this.modal.onLoad(this._onLoad)
    this.modal.subscribeToOpenEvent(this._subscribeToOpenEvent)
    this.modal.subscribeToCloseEvent(this._subscribeToCloseEvent)
    this.modal.subscribeToHoverEvent(this._subscribeToHoverEvent)

    this.props.msg.onDonationCloseInterceptReady((event) => {
      const campaignId = this._getCampaignBySource(event?.data?.source)

      if (campaignId) {
        this.closeInterceptReadyCampaigns.add(campaignId)
      }
    })

    this.props.msg.onDonationCloseConfirmed((event) => {
      const sourceCampaign = this._getCampaignBySource(event?.data?.source)
      const activeCampaign = this.modal.getCampaign() || this._getActiveCampaign()

      // Ignore confirmations coming from an iframe other than the active one.
      if (sourceCampaign && activeCampaign && sourceCampaign !== activeCampaign) {
        return
      }

      this.modal.forceClose()
    })

    this.props.msg.onDonationReady(async (event) => {
      const sourceCampaign = this._getCampaignBySource(event?.data?.source)
      const activeCampaign = this.modal.getCampaign() || this._getActiveCampaign()

      if (sourceCampaign && this.unopenedPreloads.has(sourceCampaign)) {
        return
      }

      if (sourceCampaign && activeCampaign && sourceCampaign !== activeCampaign) {
        // A cached document that doesn't own the shared messenger is asking
        // to be initialized (e.g. it reloaded while hidden). Answer its
        // iframe directly — dropping the handshake would let the document
        // hit its safeguard timeout and show the install error.
        if (this.cachedViews.get(sourceCampaign) !== this.view) {
          // Confirmed: this is the app's own DONATION_READY, so it is
          // verifiably listening and safe to mark ready.
          await this._initializeInactiveIframe(sourceCampaign, true)
        }

        return
      }

      const campaignId = sourceCampaign || activeCampaign

      // During preload, no view is active so campaignId is undefined.
      // The preload listener handles the handshake directly. Skip here
      // to avoid corrupting the shared MessengerIframe state.
      if (!campaignId) {
        return
      }

      // _handleIframeLoad is mid-handshake with this iframe and sends
      // SDK_INIT itself once the messenger is re-registered.
      if (this.reinitializingCampaigns.has(campaignId)) {
        return
      }

      this.props.msg.iframe.setAppReady()

      const configs = await this.props.configs
      const config = configs[campaignId]

      if (!config) {
        return
      }

      // Always answer DONATION_READY with SDK_INIT, even for campaigns
      // already marked ready: only a document that still needs the handshake
      // sends it (fresh load, reload, or app remount), and the app ignores
      // SDK_INIT once initialized. Withholding it would strand a reloaded
      // document whose proactive load-time SDK_INIT arrived before the app
      // attached its message listener.
      this.props.msg.iframe.sendSDKInitMessage(config, this.props.win.location.href)
      this.readyCampaigns.add(campaignId)
      this._flushHostPageEventMessages(campaignId)
      this._flushInlineGridTelemetry()

      // Track the SDK load event now that the
      // iframe is ready and registered (non-preload path).
      if (!this.eg3SDKLoadTrackedCampaigns.has(campaignId)) {
        this.eg3SDKLoadTrackedCampaigns.add(campaignId)
        this.hostPageEventInstrumentation.track(eg3SDKLoadEvent, {
          campaignId,
          data: { entry_source: 'hosted_checkout' },
        })
      }

      // If the modal was opened before the iframe finished loading,
      // the DONATION_PARAMS_UPDATE sent in _subscribeToOpenEvent
      // arrived before the app was ready to handle it.  Re-send
      // the pending params now that the app is actually ready.
      if (this.pendingParams) {
        this._sendDonationParamsUpdate(this.pendingParams)
      }
    })
    this.props.msg.onDonationCompleted(this._handleDonationCompleted)

    // Pre-load one iframe after configs resolve.
    this._preloadIframes().catch((err) => {
      console.warn('[SDK Preload] failed:', err)
    })
  }

  public sdk = (): ModalSDK => this.modal.sdk()

  public getHostPageEventInstrumentation = (): HostPageEventInstrumentation =>
    this.hostPageEventInstrumentation

  public recordInlineGridTelemetry = (record: InlineGridTelemetry): void => {
    if (this.destroyed || !isInlineGridTelemetry(record)) {
      return
    }
    this._flushInlineGridTelemetry()
    if (this.pendingInlineGridTelemetry.length >= 100) {
      return
    }
    this.pendingInlineGridTelemetry.push(record)
    this._flushInlineGridTelemetry()
  }

  private _flushInlineGridTelemetry = (): void => {
    if (!this.pendingInlineGridTelemetry.length || this.destroyed) {
      return
    }

    for (const [campaignId, view] of this.cachedViews) {
      const iframe = view.getIframe()
      if (!this.readyCampaigns.has(campaignId) || !iframe.isConnected || !iframe.contentWindow) {
        continue
      }

      const records = this.pendingInlineGridTelemetry.splice(0)
      try {
        const targetOrigin = new URL(iframe.src).origin
        records.forEach((record) =>
          iframe.contentWindow?.postMessage(
            {
              type: INLINE_GRID_TELEMETRY_MSG_TO_APP,
              payload: record,
            },
            targetOrigin
          )
        )
      } catch {
        // Best effort only: delivery must never interfere with checkout or retry.
      }
      return
    }
  }

  public destroy(): void {
    if (this.destroyed) {
      return
    }

    this.destroyed = true

    if (this.modal.getIsOpen()) {
      // Teardown must never wait on the iframe's close-intercept round trip.
      this.modal.forceClose()
    }

    this._removeAllViews()
    this._removePreloadListener()
    this.modal.destroy()
  }

  private _onLoad = (event: PubsubEvent<NavState>) => {
    if (event.data.campaign) {
      this._subscribeToOpenEvent(event)
      this.modal.open(event.data.campaign, event.data.params)
    }
  }

  private _subscribeToOpenEvent = async ({ data: { campaign, params } }: PubsubEvent<NavState>) => {
    if (!campaign) {
      return
    }

    // Remember the latest params so onDonationReady can re-send them
    // if the iframe wasn't ready when this handler ran.
    this.pendingParams = params
    const hasExplicitSelection =
      params.amount !== undefined ||
      params.frequency !== undefined ||
      params.recurring !== undefined ||
      params.currency !== undefined
    this.pendingInlineGridParams = hasExplicitSelection
      ? undefined
      : this.props.getInlineGridParams?.(campaign)

    if (this.view && this.view.getCampaign() === campaign) {
      // Active view already exists for this campaign (e.g. from hover or
      // previously closed/hidden) — just show it and push the latest
      // params via postMessage. No iframe rebuild, regardless of which
      // params changed: pass-through fields (designation, dedication,
      // c_src, c_src2, utm_*, donor) are now applied at runtime by the
      // embedded app's `useDonationParamsUpdater` hook.
      this._showView(this.view)
      this._sendDonationParamsUpdate(params)
    } else if (this.view) {
      // Active view exists but for a different campaign. Keep it cached
      // and hidden so returning to that campaign does not rebuild the iframe.
      this._deactivateCurrentView()
      await this._activateCampaign(campaign, params, true)
    } else {
      // No active view yet.
      await this._activateCampaign(campaign, params)
    }

    this._sendEmbeddedAppVisibleEvent()
  }

  /**
   * Reuse a cached view (including the single preloaded view) for the
   * given campaign, otherwise create a fresh one. In all cases the latest
   * params are pushed via postMessage so the embedded app can react
   * without an iframe reload.
   */
  private _activateCampaign = async (
    campaign: string,
    params: ClassyURLParams,
    resetMessenger = false
  ) => {
    // The preload can only register its view in `cachedViews` once configs
    // resolve. An open() issued before then (e.g. from init()'s onReady)
    // would miss the lookup and build a duplicate iframe for a campaign
    // whose preload is already in flight, orphaning it mid-handshake
    // (PRO-7917). The preload's continuation was queued first, so after
    // this await the lookup sees its view.
    await this.props.configs

    const view = this.cachedViews.get(campaign)

    if (view) {
      this.view = view
      this.unopenedPreloads.delete(campaign)

      this._showView(this.view)

      this._registerCachedView(campaign, this.view, resetMessenger)

      // Push the actual params (designation, dedication, amount, c_src, utm_*, etc.)
      // via postMessage. Ready cached views receive this immediately;
      // views still booting will receive it after DONATION_READY.
      this._sendDonationParamsUpdate(params)

      // Clean up preload listener if no more unopened preloads remain.
      if (this.unopenedPreloads.size === 0) {
        this._removePreloadListener()
      }
    } else {
      await this._appendView(campaign, params, 'visible')
    }
  }

  /**
   * Keep hidden iframe documents laid out at their final viewport size.
   *
   * A `display: none` ancestor gives the iframe a 0x0 viewport while the
   * embedded app loads. WebKit can retain the resulting invalid intrinsic
   * dimensions for responsive images after the iframe is shown, stretching
   * those images until their source is reloaded. `visibility: hidden` keeps
   * the preload non-interactive while still allowing its contents to lay out.
   */
  private _setViewVisibility = (view: ModalView, visibility: ModalViewVisibility): void => {
    view.style.display = 'block'
    view.style.visibility = visibility

    if (visibility === 'visible') {
      view.removeAttribute('aria-hidden')
    } else {
      view.setAttribute('aria-hidden', 'true')
    }
  }

  private _showView = (view: ModalView): void => {
    const { body } = this.props.win.document
    body.style.overflow = 'hidden'
    this._setViewVisibility(view, 'visible')
  }

  private _hideViewOnly = (view: ModalView): void => {
    this._setViewVisibility(view, 'hidden')
  }

  private _deactivateCurrentView = (): void => {
    if (!this.view) {
      return
    }

    const campaign = this.view.getCampaign()

    // Keep only iframes that can be shown again as-is. Un-ready views never
    // finished their handshake; completed views would resurrect a stale
    // thank-you screen instead of a fresh checkout.
    if (this.readyCampaigns.has(campaign) && !this.completedCampaigns.has(campaign)) {
      this._hideViewOnly(this.view)
      return
    }

    this._removeView()
  }

  private _registerCachedView = (
    campaign: string,
    view: ModalView,
    resetMessenger: boolean
  ): void => {
    // Switching iframe targets must clear messages queued for the previous iframe.
    if (resetMessenger) {
      this.props.msg.iframe.reset()
    }

    if (this.readyCampaigns.has(campaign)) {
      // Ready cached/preloaded iframes have already completed SDK_INIT, so
      // mark them ready before register() flushes the new queue.
      this.props.msg.iframe.setAppReady()
    }

    this.props.msg.iframe.register(view.getIframe())
  }

  private _handleIframeLoad = async (campaign: string): Promise<void> => {
    const view = this.cachedViews.get(campaign)

    if (!view) {
      return
    }

    if (!this.loadedViews.has(view)) {
      this.loadedViews.add(view)
      return
    }

    // This campaign's handshake is owned by the preload listener, not the
    // shared messenger — mirrors the same check in onDonationReady. Reacting
    // here would mark the campaign ready off a bare load event instead of a
    // confirmed DONATION_READY from the app. Checked after the first-load
    // bookkeeping above so a still-unopened preload's real first load is
    // still recorded (otherwise its actual first load would later be
    // misread as "never loaded" and mishandled once activated).
    if (this.unopenedPreloads.has(campaign)) {
      return
    }

    // Any load after the first means the document was replaced (e.g. "Make
    // another donation" reloads the iframe), so the previous donation and
    // handshake no longer apply: the new document must receive a fresh
    // SDK_INIT before its safeguard timeout expires.
    this.completedCampaigns.delete(campaign)
    this.readyCampaigns.delete(campaign)

    if (this.view !== view) {
      // The shared messenger belongs to the active view, so complete this
      // iframe's handshake directly instead. Not confirmed: this fires off
      // a bare DOM load event, not an actual DONATION_READY from the app,
      // so it must not mark the campaign ready itself.
      await this._initializeInactiveIframe(campaign, false)
      return
    }

    this.reinitializingCampaigns.add(campaign)

    try {
      this.props.msg.iframe.reset()
      this.props.msg.iframe.register(view.getIframe())

      const configs = await this.props.configs
      const config = configs[campaign]

      if (!config || this.view !== view) {
        return
      }

      this.props.msg.iframe.setAppReady()
      this.props.msg.iframe.sendSDKInitMessage(config, this.props.win.location.href)
      this.readyCampaigns.add(campaign)
      this._flushHostPageEventMessages(campaign)
      this._flushInlineGridTelemetry()

      if (this.pendingParams) {
        this._sendDonationParamsUpdate(this.pendingParams)
      }

      if (this.modal.getIsOpen()) {
        this._sendEmbeddedAppVisibleEvent()
      }
    } catch (err) {
      console.warn('[SDK] Failed to reinitialize reloaded iframe:', err)
    } finally {
      this.reinitializingCampaigns.delete(campaign)
    }
  }

  /**
   * Completes the SDK_INIT handshake for a cached iframe that does not own
   * the shared messenger (a hidden cached view, or a reload racing a view
   * switch). Mirrors the preload listener: post directly to the iframe so
   * the active campaign's messenger state is untouched. Safe for documents
   * that are already initialized — the app ignores duplicate SDK_INIT.
   *
   * `confirmed` distinguishes why this is being called: true means the app
   * itself just sent DONATION_READY, so it is verifiably listening and safe
   * to mark ready. false means this is a best-effort send off a bare iframe
   * load event — the document may not have attached its listener yet, so
   * readiness must wait for its own DONATION_READY (routed back through this
   * same method with confirmed=true) instead of being assumed here.
   */
  private _initializeInactiveIframe = async (
    campaign: string,
    confirmed: boolean
  ): Promise<void> => {
    try {
      const configs = await this.props.configs
      const config = configs[campaign]
      const view = this.cachedViews.get(campaign)

      if (!config || !view || view === this.view || !view.getIframe().contentWindow) {
        return
      }

      this._postSDKInitMessage(view, config)

      if (confirmed) {
        this.readyCampaigns.add(campaign)
        this._flushHostPageEventMessages(campaign)
        this._flushInlineGridTelemetry()
      }
    } catch (err) {
      console.warn('[SDK] Failed to initialize inactive iframe:', err)
    }
  }

  private _postSDKInitMessage = (view: ModalView, config: ConfigResponse): void => {
    view.getIframe().contentWindow?.postMessage(
      {
        type: SDK_INIT,
        payload: {
          safeguardToken: process.env.SAFEGUARD_TOKEN,
          iaa: config.iaa,
          hostUrl: this.props.win.location.href,
        },
      },
      '*'
    )
  }

  private _handleDonationCompleted = (event: PubsubEvent<MsgType>): void => {
    // Only trust the sender itself — falling back to the open/active
    // campaign could mark the wrong campaign completed when the source
    // iframe is unknown.
    const campaignId = this._getCampaignBySource(event?.data?.source)

    if (!campaignId) {
      return
    }

    // The completed document is still alive showing the thank-you screen,
    // so it keeps its ready state and the messenger keeps delivering to it.
    // The reload triggered by "Make another donation" resets the handshake
    // in _handleIframeLoad.
    this.completedCampaigns.add(campaignId)
  }

  private _sendHostPageEventMessage = (message: HostPageEventsMsgToApp): void => {
    const campaignId = message.payload.campaignId
    const view = this.cachedViews.get(campaignId)
    const iframe = view?.getIframe()

    if (iframe?.contentWindow && this.readyCampaigns.has(campaignId)) {
      iframe.contentWindow.postMessage(message, '*')
      return
    }

    this._queueHostPageEventMessage(message)
  }

  private _queueHostPageEventMessage = (message: HostPageEventsMsgToApp): void => {
    const campaignId = message.payload.campaignId
    const messages = this.pendingHostPageEventMessages.get(campaignId) || []

    messages.push(message)
    this.pendingHostPageEventMessages.set(campaignId, messages)
  }

  private _flushHostPageEventMessages = (campaignId: string): void => {
    const view = this.cachedViews.get(campaignId)
    const iframe = view?.getIframe()
    const messages = this.pendingHostPageEventMessages.get(campaignId)

    if (!iframe?.contentWindow || !messages?.length || !this.readyCampaigns.has(campaignId)) {
      return
    }

    messages.forEach((message) => iframe.contentWindow?.postMessage(message, '*'))
    this.pendingHostPageEventMessages.delete(campaignId)
  }

  private _subscribeToHoverEvent = async ({
    data: { campaign, params },
  }: PubsubEvent<NavState>) => {
    if (!campaign) {
      return
    }

    if (this.cachedViews.size === 0) {
      await this._appendView(campaign, params, 'hidden')
    }

    window.eg?.sendAnalyticsEvent?.('studio_checkout_cta_hover', { campaign, params })
  }

  private _subscribeToCloseEvent = ({ data: { campaign } }: PubsubEvent<NavState>) => {
    this._hideView()

    window.eg?.sendAnalyticsEvent?.('studio_checkout_close', {
      campaign: campaign,
    })
  }

  private _hideView = () => {
    const { body } = this.props.win.document
    body.style.overflow = 'inherit'

    if (this.view) {
      this._hideViewOnly(this.view)
    }
  }

  private _removeView = () => {
    const { body } = this.props.win.document
    body.style.overflow = 'inherit'
    const campaign = this.view?.getCampaign()

    this.view?.remove()

    if (campaign) {
      this.cachedViews.delete(campaign)
      this.unopenedPreloads.delete(campaign)
      this.readyCampaigns.delete(campaign)
      this.reinitializingCampaigns.delete(campaign)
      this.completedCampaigns.delete(campaign)
      this.pendingHostPageEventMessages.delete(campaign)
      this.closeInterceptReadyCampaigns.delete(campaign)
      if (this.view) {
        this.loadedViews.delete(this.view)
      }
    }

    this.view = undefined
  }

  private _removeAllPreloadedViews = () => {
    this.unopenedPreloads.forEach((campaign) => {
      const view = this.cachedViews.get(campaign)
      view?.remove()
      this.cachedViews.delete(campaign)
      this.readyCampaigns.delete(campaign)
      this.reinitializingCampaigns.delete(campaign)
      this.completedCampaigns.delete(campaign)
      this.pendingHostPageEventMessages.delete(campaign)
      if (view) {
        this.loadedViews.delete(view)
      }
    })
    this.unopenedPreloads.clear()
  }

  private _removeAllViews = () => {
    const views = new Set<ModalView>(this.cachedViews.values())

    if (this.view) {
      views.add(this.view)
    }

    views.forEach((view) => view.remove())
    views.forEach((view) => this.loadedViews.delete(view))
    this.cachedViews.clear()
    this.unopenedPreloads.clear()
    this.readyCampaigns.clear()
    this.reinitializingCampaigns.clear()
    this.completedCampaigns.clear()
    this.pendingHostPageEventMessages.clear()
    this.pendingInlineGridTelemetry = []
    this.closeInterceptReadyCampaigns.clear()
    this.view = undefined
  }

  private _removePreloadListener = () => {
    if (this.preloadListener) {
      this.props.win.removeEventListener('message', this.preloadListener)
      this.preloadListener = undefined
    }
  }

  private _getActiveCampaign = (): string | undefined => {
    return this.view?.getCampaign()
  }

  private _getCampaignBySource = (source?: MessageEventSource | null): string | undefined => {
    if (!source) {
      return undefined
    }

    for (const [campaignId, view] of this.cachedViews) {
      if (view.getIframe().contentWindow === source) {
        return campaignId
      }
    }

    return undefined
  }

  private _appendView = async (
    campaign: string,
    params: ClassyURLParams,
    visibility: ModalViewVisibility
  ) => {
    const { body } = this.props.win.document

    // Only lock body scroll when the modal is actually visible
    if (visibility === 'visible') {
      body.style.overflow = 'hidden'
    }

    // Get the config for this campaign
    const configs = await this.props.configs
    const config = configs[campaign]

    // A concurrent path (the preload, or another open/hover in flight) may
    // have created this campaign's view while configs resolved. Building a
    // second iframe would orphan the first mid-handshake (PRO-7917) —
    // reuse it instead.
    if (this.cachedViews.has(campaign)) {
      if (visibility === 'visible') {
        await this._activateCampaign(campaign, params)
      }
      return
    }

    // A fresh iframe must complete its own DONATION_READY handshake before
    // receiving queued messages like EMBEDDED_APP_VISIBLE.
    this.props.msg.iframe.reset()

    this.view = new ModalView({
      campaign,
      params,
      subscribeToCloseEvent: this.modal.close,
      onIframeLoad: this._handleIframeLoad,
      config,
      pgdid: this.props.pgdid,
    })

    this._setViewVisibility(this.view, visibility)

    // Append the view (and iframe) to the body
    body.appendChild(this.view)
    this.cachedViews.set(campaign, this.view)

    // Now that the iframe is appended, register the iframe with the messenger
    this.props.msg.iframe.register(this.view.getIframe())
  }

  /**
   * Chooses the single campaign we should preload.
   * Preference order:
   * 1. The active `?campaign=` already present in the URL
   * 2. The first campaign discovered in DOM order
   * 3. The first fetched config as a final fallback
   */
  private _getPreloadCampaignId = (configs: Configs): string | undefined => {
    const activeCampaign = this.modal.getCampaign()

    if (activeCampaign && configs[activeCampaign]) {
      return activeCampaign
    }

    const orderedCampaignIds = this._getCampaignIdsInDomOrder()

    for (const campaignId of orderedCampaignIds) {
      if (configs[campaignId]) {
        return campaignId
      }
    }

    return Object.keys(configs)[0]
  }

  private _getCampaignIdsInDomOrder = (): string[] => {
    const orderedCampaignIds = new Set<string>()
    const elements = this.props.win.document.querySelectorAll(
      `[${CLASSY_ATTR}], a[href*=${CLASSY_HREF}]`
    )

    elements.forEach((element) => {
      const classyCampaignId = element.getAttribute(CLASSY_ATTR)

      if (classyCampaignId) {
        orderedCampaignIds.add(classyCampaignId)
        return
      }

      if (!(element instanceof HTMLAnchorElement)) {
        return
      }

      const href = element.getAttribute('href')

      if (!href) {
        return
      }

      const hrefCampaignId = this._extractCampaignIdFromHref(href)

      if (hrefCampaignId) {
        orderedCampaignIds.add(hrefCampaignId)
      }
    })

    return [...orderedCampaignIds]
  }

  private _extractCampaignIdFromHref = (url: string): string | null => {
    if (url.startsWith(`${CLASSY_HREF}=`)) {
      return url.split('=')[1] || null
    }

    if (url.startsWith('/?') || url.startsWith('?')) {
      url = 'https://placeholder.com' + url
    }

    try {
      const urlObj = new URL(url)
      return urlObj.searchParams.get(CLASSY_HREF)
    } catch (_) {
      return null
    }
  }

  /**
   * Pre-loads a single iframe after configs resolve.
   * Creates one hidden ModalView and completes the SDK_INIT handshake
   * directly (bypassing the shared MessengerIframe) so the embedded app
   * is fully initialised before the user clicks.
   */
  private _preloadIframes = async () => {
    const configs = await this.props.configs
    const preloadCampaignId = this._getPreloadCampaignId(configs)

    if (!preloadCampaignId || this.destroyed) {
      return
    }

    // Don't preload if a view already exists (e.g., from onLoad)
    if (this.view) {
      return
    }

    const trustedOrigins = [
      process.env.EMBEDDED_APP_URI,
      process.env.EMBEDDED_APP_URI_GFM,
      // Legacy standalone embedded-app origin. Hardcoded and kept trusted during
      // the monolith migration so SDKs still served from it aren't rejected.
      // Remove once the migration is stable: https://gofundme.atlassian.net/browse/PRO-6540
      'https://giving.classy.org/embedded',
    ].filter((origin): origin is string => Boolean(origin))

    // Listen for DONATION_READY from the preloaded iframe and respond
    // with SDK_INIT directly to that iframe's contentWindow.  This avoids
    // going through the singleton MessengerIframe which can only hold one
    // iframe reference at a time.
    this.preloadListener = (event: MessageEvent) => {
      // Origin check — mirror the one in Messenger._subscribe
      const isAllowedOrigin =
        event.origin === location.origin ||
        trustedOrigins.some((origin) => origin.startsWith(event.origin))

      if (!isAllowedOrigin) {
        return
      }

      if (event.data?.type !== DONATION_READY_MSG_FROM_APP) {
        return
      }

      // Identify which preloaded iframe sent this via event.source
      for (const campaignId of this.unopenedPreloads) {
        const view = this.cachedViews.get(campaignId)

        if (!view) {
          continue
        }

        const iframe = view.getIframe()

        if (iframe.contentWindow === event.source) {
          const config = configs[campaignId]

          if (config && iframe.contentWindow) {
            // Duplicate DONATION_READY gets re-answered too — the app
            // ignores SDK_INIT once initialized, and a re-sent handshake
            // means the document actually needs one (e.g. it remounted).
            this._postSDKInitMessage(view, config)
            this.readyCampaigns.add(campaignId)
            this._flushHostPageEventMessages(campaignId)
            this._flushInlineGridTelemetry()

            // Register the hidden preload only if there is no other active
            // view. The shared MessengerIframe can point at one iframe at a
            // time, so a late preload must not steal messages from another
            // campaign the donor already opened.
            const activeCampaign = this._getActiveCampaign()

            if (!activeCampaign || activeCampaign === campaignId) {
              this.props.msg.iframe.setAppReady()
              this.props.msg.iframe.register(iframe)
            }

            // Track the SDK load event now that the
            // iframe is initialised and can receive messages when activated.
            if (!this.eg3SDKLoadTrackedCampaigns.has(campaignId)) {
              this.eg3SDKLoadTrackedCampaigns.add(campaignId)
              this.hostPageEventInstrumentation.track(eg3SDKLoadEvent, {
                campaignId,
                data: { entry_source: 'hosted_checkout' },
              })
            }
          }

          break
        }
      }
    }

    this.props.win.addEventListener('message', this.preloadListener)

    const { body } = this.props.win.document

    if (this.destroyed || this.view) {
      return
    }

    const config = configs[preloadCampaignId]
    const view = new ModalView({
      campaign: preloadCampaignId,
      params: this.modal.getParams(),
      subscribeToCloseEvent: this.modal.close,
      onIframeLoad: this._handleIframeLoad,
      config,
      pgdid: this.props.pgdid,
    })

    this._setViewVisibility(view, 'hidden')
    body.appendChild(view)
    this.cachedViews.set(preloadCampaignId, view)
    this.unopenedPreloads.add(preloadCampaignId)
  }

  private _shouldInterceptClose = (): boolean => {
    const campaignId = this.modal.getCampaign() || this._getActiveCampaign()

    return !!campaignId && this.closeInterceptReadyCampaigns.has(campaignId)
  }

  /**
   * Asks the embedded app to run its close flow (e.g. the abandoned-cart
   * opt-in UI) instead of closing right away. Falls back to force-closing
   * when the iframe can't receive the request, so the donor is never stuck
   * with a modal that won't close.
   */
  private _sendDonationCloseRequestedEvent = (): void => {
    const campaignId = this.modal.getCampaign() || this._getActiveCampaign()
    const view = campaignId ? this.cachedViews.get(campaignId) : this.view
    const iframe = view?.getIframe()

    if (!campaignId || !iframe?.contentWindow || !this.readyCampaigns.has(campaignId)) {
      this.modal.forceClose()
      return
    }

    iframe.contentWindow.postMessage(
      {
        type: DONATION_CLOSE_REQUESTED_MSG_TO_APP,
        payload: {},
      },
      '*'
    )
  }

  /**
   * Sends the `EMBEDDED_APP_VISIBLE` event to the iframe when the modal becomes visible.
   */
  private _sendEmbeddedAppVisibleEvent = () => {
    this.props.msg.iframe.sendMessage({
      type: EMBEDDED_APP_VISIBLE,
      payload: {},
    })
  }

  /**
   * Sends updated donation params to the iframe via postMessage.
   * Used when a preloaded or previously-hidden iframe is (re-)shown so the
   * embedded app can pick up amount/frequency changes that happened while
   * the iframe was not visible.
   */
  private _sendDonationParamsUpdate = (params: ClassyURLParams) => {
    this.props.msg.iframe.sendMessage({
      type: DONATION_PARAMS_UPDATE,
      payload: this.pendingInlineGridParams
        ? { params: { ...params, ...this.pendingInlineGridParams }, submit: false }
        : { params },
    })
  }
}
